import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";
import SocialLinks from "@/components/SocialLinks";
import DemoModal from "@/components/DemoModal";
import WhatsAppAssistant from "@/components/WhatsAppAssistant";
import Seo from "@/components/Seo";
import { getConfig, postLead } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const ContattiPage = () => {
  const { t, lang } = useLanguage();
  const [whatsapp, setWhatsapp] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const openDemo = useCallback(() => setDemoOpen(true), []);

  const d = t.contatti;
  const sp = t.servicePages;

  useEffect(() => {
    getConfig()
      .then((c) => setWhatsapp(c.whatsapp_number || ""))
      .catch(() => {});
  }, []);

  const waLink = useCallback(
    (text) =>
      `https://wa.me/${(whatsapp || "41782042172").replace(/\D/g, "")}?text=${encodeURIComponent(text)}`,
    [whatsapp]
  );

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postLead({ name: form.name, email: form.email, message: form.message, topic: "contatto", source: "contatti", lang });
      toast.success(d.success);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(d.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/40 focus:border-neon-cyan/60 focus:outline-none transition-colors";

  return (
    <div className="bg-void text-white font-body min-h-screen" data-testid="contatti-page">
      <Seo title={d.metaTitle} description={d.metaDesc} />
      <div className="grain-overlay" />
      <Navbar onOpenDemo={openDemo} waLink={waLink} />
      <main className="pt-16">
        <section className="pt-12 pb-16 lg:pt-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <a href="/" data-testid="contatti-back-link" className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10">
                <ArrowLeft size={16} /> {sp.back}
              </a>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" data-testid="contatti-title">
                {d.h1}
              </h1>
              <p className="mt-6 text-base lg:text-lg text-white/70 max-w-3xl">{d.subtitle}</p>
            </motion.div>
          </div>
        </section>

        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col gap-6"
            >
              <div className="glass-panel spotlight rounded-3xl p-8 sm:p-10" data-testid="contatti-info-card">
                <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{d.addressTitle}</h2>
                <div className="flex flex-col gap-4 text-white/70">
                  <p className="flex items-start gap-3">
                    <MapPin size={18} className="text-neon-cyan shrink-0 mt-1" />
                    <span>
                      <strong className="text-white">XSALES CONSULTING LTD</strong><br />
                      Via Cantonale 38, 6928 Manno TI, Svizzera
                    </span>
                  </p>
                  <a href="tel:+41782042172" data-testid="contatti-phone-link" className="flex items-center gap-3 hover:text-neon-cyan transition-colors">
                    <Phone size={18} className="text-neon-cyan shrink-0" /> +41 78 204 21 72
                  </a>
                  <a href="mailto:info@xsalesconsulting.com" data-testid="contatti-email-link" className="flex items-center gap-3 hover:text-neon-cyan transition-colors">
                    <Mail size={18} className="text-neon-cyan shrink-0" /> info@xsalesconsulting.com
                  </a>
                  <div className="pt-2">
                    <SocialLinks />
                  </div>
                </div>
                <a
                  href={waLink(d.waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contatti-whatsapp-cta"
                  className="mt-8 flex items-center justify-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-8 py-4 font-semibold text-void hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <MessageCircle size={18} /> {d.whatsappCta}
                </a>
              </div>
              <div className="glass-panel rounded-3xl overflow-hidden h-64 sm:h-72" data-testid="contatti-map">
                <iframe
                  title="XS Consulting — Via Cantonale 38, 6928 Manno TI"
                  src="https://maps.google.com/maps?q=Via%20Cantonale%2038%2C%206928%20Manno%20TI%2C%20Svizzera&output=embed"
                  className="w-full h-full border-0 grayscale-[40%] contrast-[1.05]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="glass-panel spotlight rounded-3xl p-8 sm:p-10"
            >
              <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{d.formTitle}</h2>
              <form onSubmit={submit} className="flex flex-col gap-4" data-testid="contatti-form">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={d.namePh}
                  data-testid="contatti-name-input"
                  className={inputCls}
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={d.emailPh}
                  data-testid="contatti-email-input"
                  className={inputCls}
                />
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={d.messagePh}
                  data-testid="contatti-message-input"
                  className={`${inputCls} resize-none`}
                />
                <p className="text-xs text-white/50">
                  {d.privacyNote} <a href="/privacy" className="underline hover:text-neon-cyan transition-colors">Privacy Policy</a>
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contatti-submit"
                  className="flex items-center justify-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-8 py-4 font-semibold text-void hover:opacity-90 disabled:opacity-50 transition-all duration-300"
                >
                  <Send size={18} /> {sending ? d.sending : d.submit}
                </button>
              </form>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer waLink={waLink} />
      <WhatsAppAssistant whatsapp={whatsapp} />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
};

export default ContattiPage;
